import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Navbar,
  Nav,
  Container,
  Dropdown,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
} from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Icon from './Icon';

function formatSwitcherLabel(user) {
  const name =
    user.company_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name';
  return `${user.username} - ${name}`;
}

function AppNavbar({ onToggleChat, isChatOpen, unreadCount }) {
  const { user, logout } = useAuth();

  // User switcher state (admin only)
  const [authenticatedUserRole, setAuthenticatedUserRole] = useState(null); // Track the ACTUAL logged-in user's role
  const [customerUsers, setCustomerUsers] = useState([]);
  const [blueprintUsers, setBlueprintUsers] = useState([]);
  const [otherAdminUsers, setOtherAdminUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [currentViewingUser, setCurrentViewingUser] = useState(null);

  const switchableUsers = useMemo(
    () => [...customerUsers, ...blueprintUsers, ...otherAdminUsers],
    [customerUsers, blueprintUsers, otherAdminUsers]
  );

  const switcherSections = useMemo(
    () =>
      [
        { title: 'Admin Users', users: otherAdminUsers },
        { title: 'Customer Users', users: customerUsers },
        { title: 'Blueprint Users', users: blueprintUsers },
      ].filter((section) => section.users.length > 0),
    [customerUsers, blueprintUsers, otherAdminUsers]
  );

  // Invite colleague state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState({ type: '', text: '' });
  const [fallbackUrl, setFallbackUrl] = useState('');
  const [inviteData, setInviteData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'user',
    message: '',
  });

  // Data consultant modal state
  const [showConsultantModal, setShowConsultantModal] = useState(false);
  const [assignedAdmin, setAssignedAdmin] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get('/api/user-management/current');
        const userData = response.data;

        // ALWAYS set the authenticated user's role (the actual logged-in user)
        // This should NEVER change when viewing as another user
        setAuthenticatedUserRole(userData.role);

        // Check if there's a saved viewing user ID
        const savedViewingUserId = sessionStorage.getItem('viewingAsUserId');
        if (savedViewingUserId) {
          setSelectedUserId(parseInt(savedViewingUserId));
        } else {
          setSelectedUserId(userData.id);
          setCurrentViewingUser(userData);
        }

        // If admin, fetch users for the switcher dropdown
        if (userData.role === 'admin') {
          try {
            const [customersResponse, adminsResponse] = await Promise.all([
              axios.get('/api/user-management/customers'),
              axios.get('/api/user-management/admins'),
            ]);
            const customers = customersResponse.data;
            setCustomerUsers(customers.filter((u) => !u.blueprint));
            setBlueprintUsers(customers.filter((u) => u.blueprint));
            setOtherAdminUsers(adminsResponse.data.filter((admin) => admin.id !== userData.id));

            const allSwitchableUsers = [
              ...customers.filter((u) => !u.blueprint),
              ...customers.filter((u) => u.blueprint),
              ...adminsResponse.data.filter((admin) => admin.id !== userData.id),
            ];

            // If viewing as a user, find and set that user
            if (savedViewingUserId) {
              const viewingUser = allSwitchableUsers.find(
                (u) => u.id === parseInt(savedViewingUserId)
              );
              if (viewingUser) {
                setCurrentViewingUser(viewingUser);
              } else {
                // Viewing user not found, reset to admin's own data
                sessionStorage.removeItem('viewingAsUserId');
                setSelectedUserId(userData.id);
                setCurrentViewingUser(userData);
              }
            } else {
              setCurrentViewingUser(userData);
            }
          } catch (customerError) {
            // Silently handle errors
          }
        }
      } catch (error) {
        // Silently handle errors
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  // Handle user switch
  const handleUserSwitch = async (userId) => {
    try {
      // Store the selected user ID in sessionStorage
      sessionStorage.setItem('viewingAsUserId', userId);
      setSelectedUserId(userId);

      // Find the selected user from the list
      const selectedUser = switchableUsers.find((u) => u.id === parseInt(userId));
      setCurrentViewingUser(selectedUser || user);

      // Reload the page to fetch data for the new user
      window.location.reload();
    } catch (error) {
      console.error('Error switching user:', error);
    }
  };

  // Reset to own data
  const handleResetView = () => {
    sessionStorage.removeItem('viewingAsUserId');
    setSelectedUserId(user.id);
    setCurrentViewingUser(user);
    window.location.reload();
  };

  // Fetch assigned admin data (when impersonating, use viewing user's assigned admin)
  const fetchAssignedAdmin = async () => {
    setLoadingAdmin(true);
    try {
      const viewingUserId = sessionStorage.getItem('viewingAsUserId');
      const userIdToFetch =
        viewingUserId && authenticatedUserRole === 'admin' ? parseInt(viewingUserId) : null;
      const endpoint = userIdToFetch
        ? `/api/user-management/${userIdToFetch}`
        : '/api/user-management/current';
      const response = await axios.get(endpoint);
      const userData = response.data;

      if (userData.assigned_admin_id) {
        const adminResponse = await axios.get(`/api/user-management/${userData.assigned_admin_id}`);
        setAssignedAdmin(adminResponse.data);
      } else {
        setAssignedAdmin(null);
      }
    } catch (error) {
      console.error('Error fetching assigned admin:', error);
      setAssignedAdmin(null);
    } finally {
      setLoadingAdmin(false);
    }
  };

  // Open consultant modal and fetch admin data
  const handleOpenConsultantModal = () => {
    setShowConsultantModal(true);
    fetchAssignedAdmin();
  };

  // Invite colleague functions
  const handleInviteInputChange = (e) => {
    const { name, value } = e.target;
    setInviteData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteMessage({ type: '', text: '' });
    setFallbackUrl('');

    try {
      const response = await axios.post('/api/users/invite', inviteData);
      // Backwards-compat: missing emailSent is treated as success.
      const emailSent = response.data?.emailSent !== false;

      if (emailSent) {
        const inviteeEmail = inviteData.email;
        setInviteMessage({
          type: 'success',
          text: `Invitation sent successfully to ${inviteeEmail}!`,
        });

        // Reset form
        setInviteData({
          email: '',
          first_name: '',
          last_name: '',
          role: 'user',
          message: '',
        });

        // Close modal after a short delay
        setTimeout(() => {
          setShowInviteModal(false);
          setInviteMessage({ type: '', text: '' });
        }, 2000);
      } else {
        // Partial success: row created, email failed. Surface the activation
        // link so the inviter can share it manually. Do NOT auto-close.
        const emailError = response.data?.emailError || 'unknown error';
        setInviteMessage({
          type: 'warning',
          text: `Invitation created but the activation email could not be sent (${emailError}). Share this link with ${inviteData.email} manually:`,
        });
        setFallbackUrl(response.data?.activationUrl || '');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setInviteMessage({
          type: 'danger',
          text: 'You have sent too many invites recently. Please try again later.',
        });
      } else {
        setInviteMessage({
          type: 'danger',
          text: error.response?.data?.error || 'Failed to send invitation',
        });
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyFallbackUrl = async () => {
    if (!fallbackUrl) return;
    try {
      await navigator.clipboard.writeText(fallbackUrl);
    } catch {
      // Silent — the readOnly input still allows manual select+copy.
    }
  };

  if (!user) return null;

  return (
    <Navbar
      bg="light"
      variant="light"
      expand="lg"
      fixed="top"
      className="border-bottom"
      style={{ zIndex: 1040 }}
    >
      <Container fluid>
        <div className="d-flex w-100 align-items-center position-relative">
          {/* Left side logo */}
          <Navbar.Brand as={Link} to="/dashboard" className="mb-0 fw-bold">
            <img
              src="https://www.point-of-rental.com/wp-content/uploads/2019/08/POR-Logo.png"
              alt="Point of Rental"
              height="40"
              className="me-2"
              style={{ maxWidth: '120px', objectFit: 'contain' }}
            />
          </Navbar.Brand>

          {/* Right side user menu */}
          <div className="ms-auto d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              href={`${process.env.PUBLIC_URL}/user-guide/`}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center"
              title="Open Customer User Guide"
              as="a"
            >
              <Icon name="documentText" className="me-1" />
              User Guide
            </Button>

            {/* Slack Chat Button */}
            <Button
              variant={isChatOpen ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={onToggleChat}
              className="d-flex align-items-center position-relative"
              title="Open Slack Support Chat"
            >
              <Icon name="slack" className="me-1" />
              Slack
              {!isChatOpen && unreadCount > 0 && (
                <Badge
                  bg="danger"
                  className="position-absolute top-0 start-100 translate-middle"
                  style={{ fontSize: '0.6rem' }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </Button>

            {/* User switcher for admin - ALWAYS visible if authenticated user is admin */}
            {authenticatedUserRole === 'admin' && (
              <div style={{ position: 'relative', zIndex: 10000 }}>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm" id="user-switcher">
                    <Icon name="user" className="me-1" />
                    Viewing:{' '}
                    {currentViewingUser?.company_name ||
                      currentViewingUser?.username ||
                      'Select User'}
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end" className="user-switcher-dropdown">
                    <Dropdown.Header>Switch User View</Dropdown.Header>
                    <Dropdown.Item onClick={handleResetView}>
                      <Icon name="user" className="me-2" />
                      <strong>My Data (Admin)</strong>
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    {switcherSections.map((section, index) => (
                      <React.Fragment key={section.title}>
                        <Dropdown.Header>{section.title}</Dropdown.Header>
                        {section.users.map((switchUser) => (
                          <Dropdown.Item
                            key={switchUser.id}
                            onClick={() => handleUserSwitch(switchUser.id)}
                            active={selectedUserId === switchUser.id}
                          >
                            <Icon name="user" className="me-2" />
                            {formatSwitcherLabel(switchUser)}
                          </Dropdown.Item>
                        ))}
                        {index < switcherSections.length - 1 && <Dropdown.Divider />}
                      </React.Fragment>
                    ))}
                    {switcherSections.length === 0 && (
                      <Dropdown.Item disabled>
                        <small className="text-muted">No users found</small>
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
            <Navbar.Toggle aria-controls="basic-navbar-nav" />

            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="ms-auto d-flex align-items-center gap-2">
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => setShowInviteModal(true)}
                  className="d-flex align-items-center"
                >
                  <Icon name="userPlus" className="me-1" />
                  Invite Colleague
                </Button>

                {/* Data Consultant button - visible for customers, or when admin is impersonating a customer */}
                {(authenticatedUserRole !== 'admin' ||
                  (authenticatedUserRole === 'admin' &&
                    selectedUserId &&
                    selectedUserId !== user?.id)) && (
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={handleOpenConsultantModal}
                    className="d-flex align-items-center"
                    title="Contact Your Data Consultant"
                  >
                    <Icon name="phone" className="me-1" />
                    Your Data Consultant
                  </Button>
                )}

                <Dropdown align="end">
                  <Dropdown.Toggle variant="outline-primary" size="sm" id="dropdown-basic">
                    <Icon name="user" className="me-1" />
                    {user.first_name || user.username}
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item as={Link} to="/profile">
                      <Icon name="user" className="me-2" />
                      Profile
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/settings">
                      <Icon name="cog" className="me-2" />
                      Settings
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={logout}>
                      <Icon name="x" className="me-2" />
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Nav>
            </Navbar.Collapse>
          </div>
        </div>
      </Container>

      {/* Invite Colleague Modal */}
      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} size="lg">
        <Form onSubmit={handleSendInvite}>
          <Modal.Header closeButton>
            <Modal.Title>
              <Icon name="userPlus" className="me-2" />
              Invite Colleague
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {inviteMessage.text && (
              <Alert variant={inviteMessage.type} className="mb-3">
                {inviteMessage.text}
              </Alert>
            )}

            {fallbackUrl && (
              <div className="d-flex align-items-center mt-2 mb-3">
                <Form.Control type="text" value={fallbackUrl} readOnly />
                <Button
                  variant="outline-secondary"
                  className="ms-2"
                  onClick={handleCopyFallbackUrl}
                >
                  Copy
                </Button>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={inviteData.email}
                onChange={handleInviteInputChange}
                placeholder="colleague@company.com"
                required
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={inviteData.first_name}
                    onChange={handleInviteInputChange}
                    placeholder="John"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={inviteData.last_name}
                    onChange={handleInviteInputChange}
                    placeholder="Doe"
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select name="role" value={inviteData.role} onChange={handleInviteInputChange}>
                <option value="user">User - Can view and edit data</option>
                <option value="admin">Admin - Can manage users and settings</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Personal Message (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="message"
                value={inviteData.message}
                onChange={handleInviteInputChange}
                placeholder="Add a personal message to the invitation email..."
              />
            </Form.Group>

            <Alert variant="info">
              <Icon name="informationCircle" className="me-2" />
              The invited user will receive an email with instructions to create their account and
              access the Point of Rental Data Portal.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={inviteLoading}>
              {inviteLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Icon name="paperPlane" className="me-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Data Consultant Modal */}
      <Modal show={showConsultantModal} onHide={() => setShowConsultantModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon name="phone" className="me-2" />
            Your Data Consultant
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingAdmin ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading consultant information...</p>
            </div>
          ) : assignedAdmin ? (
            <>
              <div className="mb-4">
                <h5 className="mb-3">
                  {assignedAdmin.first_name} {assignedAdmin.last_name}
                </h5>

                <div className="d-flex align-items-center mb-2">
                  <Icon name="mail" className="me-2 text-primary" />
                  <span>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${assignedAdmin.email}`}>{assignedAdmin.email}</a>
                  </span>
                </div>

                {assignedAdmin.telephone && (
                  <div className="d-flex align-items-center mb-2">
                    <Icon name="phone" className="me-2 text-primary" />
                    <span>
                      <strong>Phone:</strong>{' '}
                      <a href={`tel:${assignedAdmin.telephone}`}>{assignedAdmin.telephone}</a>
                    </span>
                  </div>
                )}

                {assignedAdmin.mobile && (
                  <div className="d-flex align-items-center mb-2">
                    <Icon name="phone" className="me-2 text-primary" />
                    <span>
                      <strong>Mobile:</strong>{' '}
                      <a href={`tel:${assignedAdmin.mobile}`}>{assignedAdmin.mobile}</a>
                    </span>
                  </div>
                )}
              </div>

              <div className="border-top pt-3">
                <p className="text-muted mb-2">
                  <small>Need immediate assistance?</small>
                </p>
                <Button
                  variant={isChatOpen ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => {
                    setShowConsultantModal(false);
                    onToggleChat();
                  }}
                  className="d-flex align-items-center justify-content-center position-relative w-100"
                >
                  <Icon name="slack" className="me-2" />
                  {isChatOpen ? 'Slack Chat Open' : 'Open Slack Support Chat'}
                  {!isChatOpen && unreadCount > 0 && (
                    <Badge
                      bg="danger"
                      className="position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: '0.6rem' }}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <Alert variant="info">
              <Icon name="informationCircle" className="me-2" />
              You don't have an assigned data consultant yet. Please contact support if you need
              assistance.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConsultantModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Navbar>
  );
}

export default AppNavbar;
